import React, { useEffect, useState } from "react";
import { meApi } from "@/lib/api";
import { PageHeader, Card } from "./_shared";
import { FileText, ExternalLink, ShieldCheck, Download } from "lucide-react";

const STATUS_LABELS = {
  brouillon: { label: "En préparation", color: "text-white/55 border-white/20" },
  envoye: { label: "Envoyé", color: "text-sky-300 border-sky-400/40" },
  signe: { label: "Signé", color: "text-emerald-300 border-emerald-400/40" },
  paye: { label: "Payé", color: "text-[#DCB457] border-[#DCB457]/50" },
};

export default function Contrat() {
  const [c, setC] = useState(null);
  useEffect(() => { meApi.contrat().then(setC).catch(() => {}); }, []);

  const status = STATUS_LABELS[c?.statut] || STATUS_LABELS.brouillon;
  const hasContrat = !!(c && c.numero);

  return (
    <div data-testid="ec-contrat">
      <PageHeader eyebrow="Documents" title="Mon Devis · Contrat" italic="& Assurance." desc="Retrouvez ici votre devis, votre contrat signé et l'attestation d'assurance professionnelle." />

      {/* RC Pro insurance — always visible for reassurance */}
      <div className="mb-6">
        <RCProAttestation />
      </div>

      {!hasContrat ? (
        <Card>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 border border-[#DCB457]/40 flex items-center justify-center text-[#DCB457] shrink-0">
              <FileText size={18} strokeWidth={1.3} />
            </div>
            <div>
              <h2 className="font-serif-luxe text-xl">Aucun contrat actif</h2>
              <p className="text-white/60 text-sm mt-2 max-w-xl">
                Votre devis sera disponible ici après validation de votre demande. Pour toute question, contactez-nous au <a href="tel:0769224956" className="text-[#DCB457] hover:underline">07 69 22 49 56</a>.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/50">Contrat n°</p>
              <p className="font-serif-luxe text-2xl mt-1" data-testid="contrat-numero">{c.numero}</p>
            </div>
            <span className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-1.5 ${status.color}`} data-testid="contrat-status">
              {status.label}
            </span>
          </div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Info label="Montant" value={c.montant || "—"} />
            <Info label="Date de signature" value={c.date_signature || "—"} />
          </dl>
          {c.notes && <p className="mt-5 text-sm text-white/65 leading-relaxed">{c.notes}</p>}
          <div className="mt-6 flex flex-wrap gap-3">
            {c.url_pdf && (
              <a href={c.url_pdf} target="_blank" rel="noopener noreferrer" data-testid="contrat-pdf" className="btn-ghost-gold inline-flex items-center gap-2">
                Télécharger le contrat <ExternalLink size={14} strokeWidth={1.4} />
              </a>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

function RCProAttestation() {
  return (
    <div
      className="card-luxe p-5 md:p-6 border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.03] to-transparent"
      data-testid="contrat-rc-pro"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/10 border border-emerald-400/30 shrink-0">
          <ShieldCheck size={20} strokeWidth={1.5} className="text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/85 mb-1">
            Sérénité incluse
          </p>
          <h3 className="font-serif-luxe text-xl md:text-2xl">
            Attestation <span className="italic gold-text">RC Pro.</span>
          </h3>
          <p className="text-white/65 text-sm mt-2 leading-relaxed max-w-2xl">
            My Animation est couvert par une assurance Responsabilité Civile
            Professionnelle. Téléchargez l'attestation officielle à joindre à
            vos éventuelles demandes de justificatifs auprès de la salle,
            mairie ou traiteur.
          </p>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-[12px]">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">N° de police</p>
              <p className="font-mono text-white/85 mt-0.5">RCP250814180127</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">Assureur</p>
              <p className="text-white/85 mt-0.5">Assurup</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">SIRET</p>
              <p className="font-mono text-white/85 mt-0.5">532 387 156 00039</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href="/documents/attestation-rc-pro.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download
              className="btn-gold text-sm inline-flex items-center gap-2"
              data-testid="contrat-rc-pro-download"
            >
              <Download size={14} strokeWidth={1.6} />
              Télécharger l'attestation
            </a>
            <a
              href="/documents/attestation-rc-pro.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost-gold text-sm inline-flex items-center gap-2"
            >
              <FileText size={14} strokeWidth={1.6} />
              Aperçu PDF
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-[0.22em] text-white/50">{label}</dt>
      <dd className="font-serif-luxe text-lg mt-1">{value}</dd>
    </div>
  );
}
